import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import { commonStyles, COLORS, formatExamDate, historyToString, calculateVolume, formatListItems, buildCombinedReason, getFacilityDisplayName } from '../utils/pdfHelpers';
import { PdfPageHeader, PdfPatientBox, PdfCallReport, PdfAccentSectionHeader, PdfSectionHeader, PdfLabelValue, PdfMeasurementBox, PdfCommentsBox, PdfFooter } from '../utils/PdfSharedComponents';
import { PdfExamDetails, PdfMaternalSections, PdfMaternalMeasurements } from '../utils/PdfMaternalSections';

function isUADopplerAbnormal(d) {
  if (!d || typeof d !== 'object') return false;
  return d.diastolicFlow === 'Absent' || d.diastolicFlow === 'Reversed';
}

function isUADopplerFilled(d) {
  if (!d || typeof d !== 'object') return false;
  return !!(d.psv || d.sdRatio || d.percentile || (d.diastolicFlow && d.diastolicFlow !== 'Present'));
}

function UADopplerRow({ label, d }) {
  const sdText    = d.sdRatio ? (d.percentile ? `S/D: ${d.sdRatio} (${d.percentile}%)` : `S/D: ${d.sdRatio}`) : '';
  const psvText   = d.psv ? `PSV: ${d.psv} cm/s` : '';
  const flowLabel = d.diastolicFlow === 'Present'  ? 'Diastolic flow (+)'
                  : d.diastolicFlow === 'Absent'   ? 'Diastolic flow (-)'
                  : d.diastolicFlow === 'Reversed' ? 'Diastolic flow (-)'
                  : '';
  const flowAbnormal = isUADopplerAbnormal(d);
  const prefix = [sdText, psvText].filter(Boolean).join(', ');
  return (
    <View style={commonStyles.row}>
      <Text style={commonStyles.label}>{label}</Text>
      <Text style={commonStyles.valueDefault}>
        {prefix ? `${prefix}, ` : ''}
        <Text style={flowAbnormal ? { color: COLORS.abnormal } : {}}>{flowLabel}</Text>
      </Text>
    </View>
  );
}

function formatEGA(ega) {
  if (!ega) return '';
  if (typeof ega === 'string') return ega;
  const w = (ega.weeks !== undefined && ega.weeks !== '') ? ega.weeks : null;
  const d = (ega.days  !== undefined && ega.days  !== '') ? ega.days  : null;
  if (w === null && d === null) return '';
  return [w !== null ? `${w}w` : null, d !== null ? `${d}d` : null].filter(Boolean).join(' ');
}

function FetalPage({ fetus, index, total, data, facilityInfo }) {
  const reason = buildCombinedReason(data.reason, data.history);
  const ga = fetus.gestationalAge || {};
  const bpp = fetus.biophysicalProfile || {};
  const anatomy = fetus.anatomy || {};
  const uaDoppler = fetus.uaDoppler || {};
  const ms = data.maternalStructures || {};

  const title = total > 1
    ? `BPP ULTRASOUND — FETUS ${String.fromCharCode(65 + index)}`
    : 'BIOPHYSICAL PROFILE ULTRASOUND WORKSHEET';

  const bppScore = [bpp.breathing, bpp.movement, bpp.tone, bpp.fluid]
    .filter(v => Number(v) === 2).length * 2;

  const fhrNum = parseFloat(fetus.fetalHeartRate);
  const fhrAbnormal = fetus.fetalHeartRate && (fhrNum < 110 || fhrNum > 160);

  // Combined placenta label
  const placentaValue = [
    fetus.placentaLocation,
    fetus.placentaGrade !== undefined && fetus.placentaGrade !== '' ? `(grade ${fetus.placentaGrade})` : null,
  ].filter(Boolean).join(' ');

  const umbilicalCordAbnormal = fetus.umbilicalCord === '2 vessel cord';
  const amnioticFluidAbnormal = fetus.amnioticFluid && fetus.amnioticFluid !== 'Normal';
  const placentaPreviaAbnormal = fetus.placentaPrevia && fetus.placentaPrevia !== 'None';

  return (
    <Page size="LETTER" style={commonStyles.page}>
      <PdfPageHeader facilityInfo={facilityInfo} examTitle={title} />
      <PdfPatientBox
        firstName={data.firstName}
        lastName={data.lastName}
        mrn={data.mrn}
        examDate={data.examDate}
        technologist={data.technologist}
        studentWorksheet={data.studentWorksheet}
        supervisingSonographer={data.supervisingSonographer}
        reason={reason}
      />
      <PdfCallReport show={data.callReport} phoneNumber={data.phoneNumber} />

      <View style={commonStyles.twoCol}>
        {/* LEFT COLUMN — MEASUREMENTS */}
        <View style={commonStyles.colLeft}>
          <PdfMeasurementBox title="Gestational Age">
            {fetus.fetalHeartRate ? (
              <Text style={[commonStyles.infoBoxText, fhrAbnormal ? { color: COLORS.abnormal } : {}]}>
                FHR: {fetus.fetalHeartRate} bpm
              </Text>
            ) : null}
            {ga.lmp ? <Text style={commonStyles.infoBoxText}>LMP: {ga.lmp}</Text> : null}
            {formatEGA(ga.clinicalEGA) ? <Text style={commonStyles.infoBoxText}>Clinical EGA: {formatEGA(ga.clinicalEGA)}</Text> : null}
            {ga.clinicalEDD ? <Text style={commonStyles.infoBoxText}>Clinical EDD: {ga.clinicalEDD}</Text> : null}
          </PdfMeasurementBox>

          <View style={commonStyles.measurementBox}>
            <Text style={[commonStyles.infoBoxText, { fontFamily: 'Helvetica-Bold', marginBottom: 4 }]}>
              BPP SCORE: {bppScore}/8
            </Text>
            <Text style={commonStyles.infoBoxText}>Breathing: {bpp.breathing ?? '—'}/2</Text>
            <Text style={commonStyles.infoBoxText}>Movement: {bpp.movement ?? '—'}/2</Text>
            <Text style={commonStyles.infoBoxText}>Tone: {bpp.tone ?? '—'}/2</Text>
            <Text style={commonStyles.infoBoxText}>Fluid: {bpp.fluid ?? '—'}/2</Text>
          </View>

          <PdfMaternalMeasurements maternal={ms} />

          <PdfCommentsBox comments={data.comments} />
        </View>

        {/* RIGHT COLUMN */}
        <View style={commonStyles.colRight}>
          <PdfExamDetails examTypeDetails={data.examTypeDetails} />

          <PdfAccentSectionHeader title="FETAL STATUS" />
          {fetus.fetalHeartRate ? (
            <PdfLabelValue label="Fetal heart rate:" value={`${fetus.fetalHeartRate} bpm`}
              isAbnormal={fhrAbnormal} forceBlack={!fhrAbnormal} />
          ) : null}
          <PdfLabelValue label="Presentation:" value={fetus.presentation || ''} forceBlack />

          <PdfAccentSectionHeader title="PLACENTA / FLUID" />
          <PdfLabelValue label="Placenta:" value={placentaValue} forceBlack />
          <PdfLabelValue label="Placenta previa:" value={fetus.placentaPrevia || 'None'}
            isAbnormal={placentaPreviaAbnormal} forceBlack={!placentaPreviaAbnormal} />
          <PdfLabelValue label="Umbilical cord:" value={fetus.umbilicalCord || ''}
            isAbnormal={umbilicalCordAbnormal} forceBlack={!umbilicalCordAbnormal} />
          <PdfLabelValue label="Amniotic fluid:" value={fetus.amnioticFluid || ''}
            isAbnormal={amnioticFluidAbnormal} forceBlack={!amnioticFluidAbnormal} />

          <PdfMaternalSections maternal={ms} />


          <PdfAccentSectionHeader title="ANATOMY" />
          {anatomy.bladder ? <PdfLabelValue label="Bladder:" value={anatomy.bladder}
            isAbnormal={anatomy.bladder !== 'Seen - Normal'} forceBlack={anatomy.bladder === 'Seen - Normal'} /> : null}
          {anatomy.kidneys ? <PdfLabelValue label="Kidneys:" value={anatomy.kidneys}
            isAbnormal={anatomy.kidneys !== 'Seen - Normal'} forceBlack={anatomy.kidneys === 'Seen - Normal'} /> : null}
          {anatomy.fourCH ? <PdfLabelValue label="4-chamber heart:" value={anatomy.fourCH}
            isAbnormal={anatomy.fourCH !== 'Seen - Normal'} forceBlack={anatomy.fourCH === 'Seen - Normal'} /> : null}
          {anatomy.stomach ? <PdfLabelValue label="Stomach:" value={anatomy.stomach}
            isAbnormal={anatomy.stomach !== 'Seen - Normal'} forceBlack={anatomy.stomach === 'Seen - Normal'} /> : null}

          {(isUADopplerFilled(uaDoppler.fetal) || isUADopplerFilled(uaDoppler.midCord) || isUADopplerFilled(uaDoppler.placenta)) ? (
            <PdfAccentSectionHeader title="UA DOPPLER" />
          ) : null}
          {isUADopplerFilled(uaDoppler.fetal) && <UADopplerRow label="Fetal end:" d={uaDoppler.fetal} />}
          {isUADopplerFilled(uaDoppler.midCord) && <UADopplerRow label="Mid-cord:" d={uaDoppler.midCord} />}
          {isUADopplerFilled(uaDoppler.placenta) && <UADopplerRow label="Placental end:" d={uaDoppler.placenta} />}
        </View>
      </View>

      <PdfFooter facilityInfo={facilityInfo} />
    </Page>
  );
}

export function BPPPDF({ data, facilityInfo }) {
  const fetuses = data.fetuses || [];
  const total = fetuses.length || 1;

  return (
    <Document>
      {fetuses.length > 0 ? fetuses.map((fetus, i) => (
        // onlyFetusIndex: PNG output renders one fetus per document so pages can be named per fetus
        data.onlyFetusIndex == null || data.onlyFetusIndex === i
          ? <FetalPage key={i} fetus={fetus} index={i} total={total} data={data} facilityInfo={facilityInfo} />
          : null
      )) : (
        <FetalPage fetus={{}} index={0} total={1} data={data} facilityInfo={facilityInfo} />
      )}
    </Document>
  );
}
